import React, { useCallback, useEffect, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import { telefonoBonito } from "../../../utils/telefonoBonito";
import { Campaign, CampaignContact, DialerRow, ProspectCatalogs } from "../../../services/v2/prospects.service";
import { cuando, errorTexto, nombreCompleto, statusClase } from "./salesHelpers";
import ClienteView from "./clienteView";
import { useTelefono } from "./telefono/telefonoUI";

interface Props {
	campusId: number;
	catalogs: ProspectCatalogs | null;
	etapa: number;
	owner: string;
	onSalir: () => void;
}

/**
 * Marcador (Esteban, 2026-09-13): la asesora llama a SU cartera, en orden de probabilidad — seguimientos vencidos,
 * quien escribió hace poco, los nuevos, las promesas — y no pierde tiempo buscando a quién llamar. Cada llamada
 * queda registrada sola; al guardar cómo terminó pasa a la siguiente persona.
 */
const MarcadorView: React.FC<Props> = ({ campusId, catalogs, etapa, owner, onSalir }) => {
	const http = HttpManager.getInstance();
	const [cola, setCola] = useState<DialerRow[]>([]);
	const [total, setTotal] = useState<number>(0);
	const [actual, setActual] = useState<number | null>(null);
	const [hechas, setHechas] = useState<{ [id: number]: string }>({});
	const [auto, setAuto] = useState<boolean>(false);
	const [cargando, setCargando] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	// Campañas (las arma supervisión): la asesora elige una y le va tocando "la siguiente".
	const tel = useTelefono();
	const [campanas, setCampanas] = useState<Campaign[]>([]);
	const [campana, setCampana] = useState<Campaign | null>(null);
	const [contacto, setContacto] = useState<CampaignContact | null>(null);
	const [hechasC, setHechasC] = useState<{ nombre: string; res: string }[]>([]);
	const [espera, setEspera] = useState<string | null>(null);
	useEffect(() => {
		http.prospectsService.campaigns(campusId).then((r) => setCampanas(r.data.campaigns.filter((c) => c.status === "activa" && c.soyAgente))).catch(() => setCampanas([]));
	}, [campusId]);
	const siguienteC = async (c: Campaign) => {
		setError(null); setEspera(null);
		try {
			const r = await http.prospectsService.campaignNext(campusId, c.id);
			setContacto(r.data.contact);
			if (!r.data.contact) setEspera(r.data.proximoReintento ? "Por ahora nadie. Vuelven a salir desde " + cuando(r.data.proximoReintento) + "." : "Terminaste tu parte de la campaña.");
		} catch (e) { setContacto(null); setError(errorTexto(e)); }
	};
	const saltarC = async () => {
		if (!campana || !contacto) return;
		try { await http.prospectsService.campaignSkip(campusId, contacto.id); } catch (e) { /* igual pasa a la siguiente */ }
		siguienteC(campana);
	};

	const cargar = useCallback(async () => {
		setCargando(true); setError(null);
		try {
			const r = await http.prospectsService.dialer(campusId, { etapa, owner }, 80);
			setCola(r.data.queue); setTotal(r.data.total);
		} catch (e) { setError(errorTexto(e)); }
		finally { setCargando(false); }
	}, [campusId, etapa, owner]);

	useEffect(() => { cargar(); setActual(null); setHechas({}); }, [cargar]);

	const pendientes = cola.filter((r) => !hechas[r.id]);
	const siguiente = (despuesDe: number | null) => {
		const resto = cola.filter((r) => !hechas[r.id] && r.id !== despuesDe);
		setActual(resto.length ? resto[0].id : null);
		if (!resto.length) cargar();
	};
	const empezar = () => { setAuto(true); if (pendientes.length) setActual(pendientes[0].id); };
	const saltar = () => { if (actual) { setHechas({ ...hechas, [actual]: "saltado" }); siguiente(actual); } };

	if (campana) {
		const automatico = campana.pacing === "automatico";
		return (
			<div className="ventas-cuerpo marcador">
				<div className="mc-cola">
					<div className="mc-cab">
						<div><b>{campana.name}</b><span className="apagado"> · {campana.mode === "grupal" ? "fila del equipo" : "tu gente"} · {automatico ? "automático" : "vista previa"}</span></div>
						<button className="enlace" onClick={() => { setCampana(null); setContacto(null); setHechasC([]); }}>Salir de la campaña</button>
					</div>
					<div className="mc-acciones">
						{!contacto ? <button className="btn-wa pri" onClick={() => siguienteC(campana)}>{hechasC.length ? "Siguiente" : "Empezar"}</button> : <button className="btn-wa" onClick={saltarC}>Saltar</button>}
						{automatico && !tel.listo ? <span className="oro" style={{ fontSize: 12 }}>Enciende el teléfono para que marque solo</span> : null}
					</div>
					{error ? <div className="aviso error" style={{ margin: 10 }}>{error}</div> : null}
					{espera ? <div className="aviso" style={{ margin: 10 }}>{espera}</div> : null}
					<div className="filas">
						{contacto ? (
							<div className="ventas-fila activa">
								<div className="l1"><span className="nombre">{nombreCompleto(contacto.prospect)}</span><span className="cuando">intento {contacto.attempts + 1}</span></div>
								<div className="l2">{[telefonoBonito(contacto.prospect.phone_e164), contacto.prospect.course_interest_text].filter(Boolean).join(" · ")}</div>
								<div className="chips"><span className={"chip " + statusClase(contacto.prospect.status_id)}>{contacto.prospect.status}</span></div>
							</div>
						) : null}
						{hechasC.slice().reverse().map((h, i) => <div key={i} className="ventas-fila hecha"><div className="l1"><span className="nombre">{h.nombre}</span><span className="cuando">{h.res.replace("_", " ")}</span></div></div>)}
					</div>
				</div>
				{contacto ? (
					<ClienteView key={contacto.id} campusId={campusId} prospectId={contacto.prospectId} catalogs={catalogs} onChanged={() => { /* la campaña lleva la cuenta */ }}
						onOpen={() => { /* en campaña se queda en la persona */ }} onBack={() => setContacto(null)}
						marcador={{ autoLlamar: automatico, campaignContactId: contacto.id, onResultado: (res) => {
							setHechasC((h) => [...h, { nombre: nombreCompleto(contacto.prospect), res }]); setContacto(null); siguienteC(campana);
						} }} />
				) : (
					<div className="ventas-cliente"><div className="ventas-vacio mc-inicio">
						<h3>{campana.name}</h3>
						<p>{automatico ? "Al terminar cada llamada y guardar cómo terminó, marca sola a la siguiente." : "Te sale la ficha de la siguiente persona; revisa y dale Llamar."}</p>
						<button className="btn-wa pri" onClick={() => siguienteC(campana)}>{hechasC.length ? "Siguiente" : "Empezar"}</button>
					</div></div>
				)}
			</div>
		);
	}

	return (
		<div className="ventas-cuerpo marcador">
			<div className="mc-cola">
				<div className="mc-cab">
					<div>
						<b>Auto-marcador</b>
						<span className="apagado"> · {total.toLocaleString("es-MX")} por llamar</span>
					</div>
					<button className="enlace" onClick={onSalir}>Volver a la bandeja</button>
				</div>
				{campanas.length ? (
					<div className="mc-campanas">
						<span className="apagado">Campañas para ti:</span>
						{campanas.map((c) => <button key={c.id} className="btn-wa mini" onClick={() => { setCampana(c); setContacto(null); setHechasC([]); setEspera(null); }}>{c.name}</button>)}
					</div>
				) : null}
				<div className="mc-acciones">
					{!auto ? <button className="btn-wa pri" disabled={!pendientes.length} onClick={empezar}>Empezar a llamar</button>
						: <button className="btn-wa" onClick={() => setAuto(false)}>Pausar</button>}
					{auto && actual ? <button className="btn-wa" onClick={saltar}>Saltar</button> : null}
					<button className="btn-wa" onClick={cargar} disabled={cargando}>Actualizar</button>
				</div>
				{error ? <div className="aviso error" style={{ margin: 10 }}>{error}</div> : null}
				<div className="filas">
					{cola.map((r, i) => (
						<button key={r.id} className={"ventas-fila" + (actual === r.id ? " activa" : "") + (hechas[r.id] ? " hecha" : "")} onClick={() => setActual(r.id)}>
							<div className="l1">
								<span className="nombre"><span className="mc-num">{i + 1}</span>{nombreCompleto(r)}</span>
								{hechas[r.id] ? <span className="cuando">{hechas[r.id] === "saltado" ? "saltado" : "✓"}</span> : null}
							</div>
							<div className="l2">{[telefonoBonito(r.phone_e164), r.course_interest_text].filter(Boolean).join(" · ")}</div>
							<div className="chips">
								<span className={"chip " + statusClase(r.status_id)}>{r.status}</span>
								{r.reasons.filter((x) => x !== r.status).slice(0, 3).map((x) => <span key={x} className="chip razon">{x}</span>)}
							</div>
						</button>
					))}
					{!cargando && !cola.length ? <div className="ventas-vacio">Nadie por llamar ahora. Los que no contestaron vuelven a salir en 2 horas.</div> : null}
				</div>
			</div>
			{actual ? (
				<ClienteView key={actual} campusId={campusId} prospectId={actual} catalogs={catalogs} onChanged={() => { /* la cola se refresca al terminar */ }}
					onOpen={setActual} onBack={() => setActual(null)}
					marcador={{ autoLlamar: auto, onResultado: (res) => { setHechas((h) => ({ ...h, [actual]: res })); if (auto) siguiente(actual); } }} />
			) : (
				<div className="ventas-cliente">
					<div className="ventas-vacio mc-inicio">
						<h3>Tu lista de llamadas de hoy</h3>
						<p>Primero los seguimientos que vencen, luego quien escribió hace poco, los nuevos y las promesas. Quien no contestó 6 veces seguidas sale de la lista.</p>
						<p>{tel.listo ? "El teléfono está encendido: al dar Empezar marca solo a cada persona y al guardar cómo terminó pasa a la siguiente."
							: "Enciende el teléfono (arriba) para que marque solo. Sin teléfono te abre a cada persona para que marques y anotes cómo terminó."}</p>
						<p className="apagado">Esta lista es de gente con movimiento en los últimos 60 días. Para retirados, graduados o listas grandes, supervisión arma una Campaña.</p>
						<button className="btn-wa pri" disabled={!pendientes.length} onClick={empezar}>Empezar a llamar</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default MarcadorView;
